/**
 * Formatting utilities for chart data display
 */

// Convert string or number to number, handling comma separators
export const toNumber = (value?: string | number | null): number => {
  if (value === undefined || value === null) return 0;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Truncate number to specified decimal places without rounding
 * @param num The number to truncate
 * @param decimals The number of decimal places to keep (default is 2)
 * @returns The truncated number without rounding
 */
export const truncateNumber = (num: number, decimals: number = 2): number => {
  if (decimals === 0) {
    return Math.trunc(num);
  }
  const multiplier = 10 ** decimals;
  return Math.trunc(num * multiplier) / multiplier;
};

// Format number with thousands separators
export function formatNumberWithSeparators(
  value: number,
  decimalPlaces: number = 0,
): string {
  const truncated = truncateNumber(value, decimalPlaces);
  // Convert to string without rounding
  const parts = truncated.toString().split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1] || "";
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (decimalPlaces > 0) {
    const paddedDecimals = decimalPart.padEnd(decimalPlaces, "0");
    return `${formattedInteger}.${paddedDecimals}`;
  }
  return formattedInteger;
}

/**
 * Formats x-axis labels by replacing the first hyphen or space with a line break
 * Converts "MMM-YYYY" to "MMM<br/>YYYY" for multi-line display
 * Also splits long labels (>10 chars) at the first space for better readability
 */
export function formatXAxisLabel(value: string): string {
  if (!value.includes("+") && value.includes("-")) {
    return value.replace("-", "<br/>");
  }
  if (value.length > 10 && value.includes(" ")) {
    return value.replace(" ", "<br/>");
  }
  return value;
}
